import { CSSProperties, useEffect, useState } from 'react';
import { TextField, CircularProgress, Switch } from '@material-ui/core';
import useCampaignBuilder from 'Hooks/useCampaignBuilder';
import { clsx, makeKey, styx } from 'modules/Utils';
import Sty, { Container } from 'modules/Sty';
import { Body1, Body2 } from 'components/shareable/Typography';
import Colors from 'modules/Colors';
import InfoSection from 'components/InfoSection';
import RangeSlider from 'components/shareable/Slider/RangeSlider';
import DropdownString from 'components/shareable/DropDown/DropDownString';
import DropdownChip from 'pages/MarketingPage/Components/TargetDemographics/DropDownChip';
import { TargetedLanguage } from 'modules/Types';
import CardSymphony from 'elements/CardSymphony';
import SwitchSymphony, { SwitchType } from 'elements/SwitchSymphony';
import SliderSymphony from 'elements/SliderSymphony';
import InputSymphony from 'elements/InputSymphony';
import TagSymphony from 'elements/TagSymphony';
import useScreen from 'Hooks/useScreen';
import { FB_DEFAULT_GENDERS, FB_DEFAULT_MAX_AGE, FB_DEFAULT_MIN_AGE } from 'modules/Campaign';
import { getLocales } from 'api/facebookApi';
import FB from 'helpers/FB';

export const FB_CAMPAIGNS_GENDERS = {
    ALL: 'All',
    MALE: 'Male',
    FEMALE: 'Female'
}

interface TargetingCampaignView_DEMOGRAPHICSProps {
    className?: string;
    style?: CSSProperties;
    platform: 'tiktok' | 'facebook'
}

const TargetingCampaignView_DEMOGRAPHICS = ({
    className = '',
    style = {},
    platform = 'tiktok'
}: TargetingCampaignView_DEMOGRAPHICSProps) => {
    const { mobileView, mStyle } = useScreen();
    const {
        minAge,
        maxAge,
        genders: targetedGenders,
        languages,
        isDemographicsAutomated,
        setMinAge,
        setMaxAge,
        setTargetedGender,
        setLanguages,
        setIsDemographicsAutomated,
        setDemographics,
    } = useCampaignBuilder();


    const [isEditable, setIsEditable] = useState<boolean>(true);

    // language search
    const [languageText, setLanguageText] = useState<string>('');
    const [availableLanguages, setAvailableLanguages] = useState<TargetedLanguage[]>([]);
    const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
    
    // Fetch languages when search text changes
    useEffect(() => {
        const fetchLanguages = async () => {
            if (languageText.length < 2) {
                setAvailableLanguages([]);
                return;
            }
            
            setIsLoadingLanguages(true);
            try {
                const authResponse = FB()?.getAuthResponse();
                const accessToken = authResponse?.accessToken;
                
                const locales = await getLocales({
                    locales: languageText,
                    accessToken
                });
                
                setAvailableLanguages(locales.map(locale => ({
                    key: locale.key,
                    name: locale.name
                })));
            } catch (error) {
                console.error('Error fetching languages:', error);
            } finally {
                setIsLoadingLanguages(false);
            }
        };
        
        const debounceTimer = setTimeout(fetchLanguages, 300);
        return () => clearTimeout(debounceTimer);
    }, [languageText]);

    const ageMarks = [
        { value: 18, label: '18', description: '', placement: 'first' as const },
        { value: 30, label: '30', description: '', placement: 'middle' as const },
        { value: 50, label: '50', description: '', placement: 'middle' as const },
        { value: 65, label: '65', description: '', placement: 'last' as const }
    ]

    const handleAutomatedTargeting = () => {
        setIsDemographicsAutomated(!isDemographicsAutomated);
    };

    const formatLanguagesForSelect = (languages: TargetedLanguage[]) => {
        return languages.map((language) => ({
            value: language.key.toString(),
            title: language.name,
            object: language
        }));
    }

    const renderAgeSection = () => {
        return (
            <div className="ageSection">
                <div className="ageSectionTitleContainer" style={{
                    ...sty.sectionTitle,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div className="ageSectionTitle" >
                        Targeted Ages:
                    </div>
                    <div
                        className="ageScaleSectionTitle"
                        style={styx(
                            sty.ageScaleSectionTitle,
                            { backgroundColor: isEditable ? Colors.purpleLightest : Colors.greyLighter }
                        )}
                    >
                        {minAge} to {maxAge}
                    </div>
                </div>
                <SliderSymphony
                    className="ageSectionSlider"
                    sliderOffset={1}
                    marks={ageMarks}
                    value={[
                        minAge,
                        maxAge
                    ]}
                    min={FB_DEFAULT_MIN_AGE}
                    max={FB_DEFAULT_MAX_AGE}
                    onChange={(values) => {
                        const [newMin, newMax] = values as [number, number]
                        setMinAge(newMin)
                        setMaxAge(newMax)
                    }}
                    disabled={!isEditable}
                    isRange
                />
            </div>
        )
    }

    const renderGenderSection = () => {

        console.log('targetedGenders', targetedGenders)

        const renderedSwitches = FB_DEFAULT_GENDERS.map((gender) => {

            let isSelectedForCampaign = Boolean(targetedGenders.find((g) => g.key === gender.key))

            let onChange = () => {
                if (isSelectedForCampaign) {
                    setTargetedGender(targetedGenders.filter((g) => g.key !== gender.key))
                } else {
                    setTargetedGender([...targetedGenders, gender])
                }
            }
            return (
                <SwitchSymphony
                    className="genderSectionSwitch"
                    checked={isSelectedForCampaign}
                    onChange={onChange}
                    disabled={!isEditable}
                    label={gender.name}
                    labelPlacement="right"
                    type={SwitchType.CHECKBOX}
                />
            )
        })
        return (
            <div className="genderSection">
                <div className="genderSectionTitle" style={sty.sectionTitle}>
                    Targeted Genders:
                </div>
                <div className="genderSectionSwitches" style={sty.genderSectionSwitches}>
                    {renderedSwitches}
                </div>
            </div>
        )
    }

    const renderLanguageSection = () => {
        if (platform === 'tiktok') return null;
        return (
            <div className="languageSection">
                <div style={sty.sectionTitle}>
                    Targeted Languages:
                </div>
                {isEditable &&
                    <InputSymphony
                        className="languageSectionTextInput"
                        value={languageText}
                        onChange={(valueText) => setLanguageText(valueText)}
                        placeholder="e.g. English, Spanish, etc."
                        isSelect
                        isLoading={isLoadingLanguages}
                        fetchSelectedOptions={async () => {
                            if (languageText.length >= 2) {

                                const locales = await getLocales({
                                    locales: languageText,
                                });
                                
                                setAvailableLanguages(locales.map(locale => ({
                                    key: locale.key,
                                    name: locale.name,
                                    selected: false
                                })));
                            }
                            return [];
                        }}
                        onOptionSelect={(value) => {
                            const selectedLanguage = value.object as TargetedLanguage;
                            if (selectedLanguage && !languages.some(lang => lang.key === selectedLanguage.key)) {
                                setLanguages([...languages, selectedLanguage]);
                            }
                            setLanguageText('');
                        }}
                        options={formatLanguagesForSelect(availableLanguages)}
                    />
                }
                <div className="selectedLanguagesContainer" style={sty.selectedLanguagesContainer}>
                    {languages.map((language, index) => (
                        <TagSymphony
                            key={makeKey('selected-language-tag', language.key, index)}
                            className="selectedLanguageTag"
                            title={language.name}
                            id={language.key.toString()}
                            color={isEditable ? 'purple' : 'grey'}
                            onRemove={isEditable ?
                                () => setLanguages(languages.filter((l) => l.key !== language.key))
                                :
                                undefined
                            }
                        />
                    ))}
                </div>
            </div>
        )
    }

    return (
        <CardSymphony
            className={clsx('targetingCampaignView_DEMOGRAPHICSComponent', className)}
            style={{
                ...style,
                padding: 0,
                marginBottom: 16,
                gap: 24
            }}
        >
            {renderAgeSection()}
            {renderGenderSection()}
            {renderLanguageSection()}
        </CardSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    infoSection: {
        padding: '0 40px',
        width: '100%'
    },
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    iconContainer: {
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8
    },
    sectionTitle: {
        ...Sty.text18bold,
        marginBottom: 8
    },
    searchSection: {
        width: '100%'
    },
    optionContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    chipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 16,
    },
    demographicsSection: {
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 16,
        marginTop: 24,
        width: '100%'
    },
    automatedSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: Colors.purpleLightest,
        borderRadius: 8
    },
    automatedDescription: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    automatedIcon: {
        flexShrink: 0,
        width: 24,
        height: 24
    },
    inlineIcon: {
        width: 16,
        height: 16,
        marginLeft: 8
    },
    demographicsControls: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    sliderContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        paddingTop: 40
    },
    genderSectionSwitches: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16
    },
    ageSectionSliderContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        marginTop: 16
    },
    ageScaleSectionTitle: {
        ...Sty.text16bold,
        borderRadius: 8,
        paddingTop: 8,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
    },
    ageSectionTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    selectedLanguagesContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 16,
    }
};

export default TargetingCampaignView_DEMOGRAPHICS;